import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LuWifiOff } from 'react-icons/lu';
import type { LessonInfo } from '../../shared/protocol';
import { effectiveCanDraw } from '../../shared/protocol';
import { CenterCard, Toast } from '../components/ui';
import { LessonConnection } from '../lib/connection';
import { History } from '../lib/history';
import { lesson, lessonStore } from '../lib/lesson';
import { openPdf, type PDFDocumentProxy } from '../lib/pdf';
import { storage, studentLink } from '../lib/storage';
import { useStore } from '../lib/store';
import { setTool, toolStore, type Tool } from '../lib/tools';
import { clamp, copyText } from '../lib/util';
import { PageView, type FitMode } from './PageView';
import { ParticipantsPanel } from './Participants';
import { Toolbar } from './Toolbar';
import { TopBar } from './TopBar';

const KEY_TOOLS: Record<string, Tool> = { v: 'pan', p: 'pen', h: 'highlighter', e: 'eraser', l: 'line', a: 'arrow', r: 'rect', o: 'ellipse', t: 'text', k: 'laser' };

function useElementSize(el: HTMLElement | null) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!el) return;
    let timer = 0;
    const measure = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    measure();
    // Pencere boyutu değişirken PDF'yi her karede yeniden çizmemek için kısa gecikme.
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = window.setTimeout(measure, 120);
    });
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, [el]);
  return size;
}

export function LessonRoom({ lessonId, teacherKey, name, info }: { lessonId: string; teacherKey?: string; name: string; info: LessonInfo }) {
  const status = useStore(lessonStore, (s) => s.status);
  const everOnline = useStore(lessonStore, (s) => s.everOnline);
  const fatal = useStore(lessonStore, (s) => s.fatal);
  const self = useStore(lessonStore, (s) => s.self);
  const settings = useStore(lessonStore, (s) => s.settings);
  const participants = useStore(lessonStore, (s) => s.participants);
  const history = useMemo(() => new History(), []);
  const canUndo = useStore(history.state, (s) => s.canUndo);
  const canRedo = useStore(history.state, (s) => s.canRedo);
  const tool = useStore(toolStore, (s) => s.tool);

  const [conn, setConn] = useState<LessonConnection | null>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [localPage, setLocalPage] = useState(1);
  const [follow, setFollow] = useState(true);
  const [fit, setFit] = useState<FitMode>(() => (window.innerWidth > window.innerHeight * 1.15 ? 'page' : 'width'));
  const [zoom, setZoom] = useState(1);
  const [panelOpen, setPanelOpen] = useState(() => window.innerWidth >= 1100);
  const [exporting, setExporting] = useState(false);
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  const viewport = useElementSize(scrollEl);

  // Bağlantı
  useEffect(() => {
    lesson.reset();
    const c = new LessonConnection(
      { t: 'hello', lessonId, clientId: storage.clientId(), name, teacherKey },
      (msg) => lesson.apply(msg),
      (s, detail) => lesson.status(s, detail),
    );
    setConn(c);
    return () => {
      c.close();
      lesson.reset();
    };
  }, [lessonId, teacherKey, name]);

  // PDF
  useEffect(() => {
    const task = openPdf(`/api/lessons/${lessonId}/pdf`, setPdfProgress);
    task.promise.then(setDoc, (err: unknown) => setPdfError(err instanceof Error ? err.message : String(err)));
    return () => {
      void task.destroy();
    };
  }, [lessonId]);

  // Lazer izleri ve takılı önizlemeler için temizlik
  useEffect(() => {
    const t = setInterval(() => lesson.prune(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  const isTeacher = self?.role === 'teacher';
  const me = participants.find((p) => p.id === self?.id) ?? self;
  const permitted = !!me && effectiveCanDraw(me, settings);
  const canDraw = permitted && status === 'online';
  const pageCount = Math.max(1, doc?.numPages ?? info.pageCount ?? 1);
  const following = !isTeacher && (settings.followLocked || follow);
  const page = clamp(isTeacher ? localPage : following ? settings.teacherPage : localPage, 1, pageCount);

  // Öğretmen: derse dönünce kaldığı sayfa; ikinci cihazı (ör. tablet) varsa onu da izler.
  useEffect(() => {
    if (isTeacher) setLocalPage(settings.teacherPage);
  }, [isTeacher, settings.teacherPage]);

  // Öğrenciye izin verilince kalemi seç ve haber ver.
  const prevPermitted = useRef<boolean | null>(null);
  useEffect(() => {
    if (!self) return;
    if (prevPermitted.current !== null && !isTeacher && prevPermitted.current !== permitted) {
      if (permitted) {
        setTool('pen');
        lesson.toast('Öğretmen size çizim izni verdi.');
      } else lesson.toast('Çizim izniniz kaldırıldı.');
    }
    prevPermitted.current = permitted;
  }, [permitted, self, isTeacher]);

  const goTo = useCallback(
    (p: number) => {
      const n = clamp(p, 1, pageCount);
      if (isTeacher) {
        setLocalPage(n);
        conn?.send({ t: 'nav', page: n });
      } else if (!following) setLocalPage(n);
    },
    [conn, isTeacher, following, pageCount],
  );

  const toggleFollow = () => {
    if (settings.followLocked) return;
    if (follow) setLocalPage(settings.teacherPage);
    setFollow(!follow);
  };

  const clearPage = () => {
    if (!conn || !isTeacher) return;
    const anns = lessonStore.get().pages[page] ?? [];
    if (!anns.length) return lesson.toast('Bu sayfada silinecek çizim yok.');
    if (!window.confirm(`${page}. sayfadaki tüm çizimler (${anns.length}) silinsin mi? Geri al ile geri getirebilirsiniz.`)) return;
    if (!conn.send({ t: 'page:clear', page })) return;
    lesson.remove(anns.map((a) => a.id));
    history.push({ kind: 'remove', anns });
  };

  const copyLink = async () => {
    const ok = await copyText(studentLink(lessonId));
    lesson.toast(ok ? 'Öğrenci bağlantısı kopyalandı.' : `Bağlantı: ${studentLink(lessonId)}`);
  };

  const exportPdf = async () => {
    if (!teacherKey || exporting) return;
    setExporting(true);
    try {
      const res = await fetch(`/api/lessons/${lessonId}/export?key=${encodeURIComponent(teacherKey)}`);
      if (!res.ok) throw new Error(((await res.json().catch(() => null)) as { error?: string } | null)?.error ?? 'PDF hazırlanamadı.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${info.title} - ders notları.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      lesson.toast(err instanceof Error ? err.message : 'PDF hazırlanamadı.');
    } finally {
      setExporting(false);
    }
  };

  // Klavye kısayolları
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return;
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (mod && key === 'z') {
        e.preventDefault();
        if (e.shiftKey) history.redo(conn);
        else history.undo(conn);
        return;
      }
      if (mod && key === 'y') {
        e.preventDefault();
        history.redo(conn);
        return;
      }
      if (mod || e.altKey) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') return goTo(page + 1);
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') return goTo(page - 1);
      const t = key === 'g' ? toolStore.get().polygon : KEY_TOOLS[key];
      if (t && (t === 'pan' || (t === 'laser' ? isTeacher : permitted))) setTool(t);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [conn, history, goTo, page, isTeacher, permitted]);

  if (fatal) {
    return (
      <CenterCard title="Derse bağlanılamadı" text={fatal}>
        <a className="btn btn-primary" href="/">
          Ana sayfa
        </a>
      </CenterCard>
    );
  }
  if (pdfError) {
    return (
      <CenterCard title="PDF açılamadı" text={pdfError}>
        <a className="btn btn-primary" href="/">
          Ana sayfa
        </a>
      </CenterCard>
    );
  }

  const students = participants.filter((p) => p.role === 'student');

  return (
    <div className="room">
      <TopBar
        title={info.title}
        page={page}
        pageCount={pageCount}
        navDisabled={!isTeacher && following}
        onGo={goTo}
        isTeacher={isTeacher}
        following={following}
        followLocked={settings.followLocked}
        onToggleFollow={toggleFollow}
        fit={fit}
        onFit={setFit}
        zoom={zoom}
        onZoom={setZoom}
        status={status}
        onCopyLink={copyLink}
        onExport={exportPdf}
        exporting={exporting}
        panelOpen={panelOpen}
        onTogglePanel={() => setPanelOpen((v) => !v)}
        onlineStudents={students.filter((s) => s.online).length}
        raisedHands={isTeacher ? students.filter((s) => s.handRaised).length : 0}
      />
      <div className="room-body">
        <Toolbar
          isTeacher={isTeacher}
          permitted={permitted}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={() => history.undo(conn)}
          onRedo={() => history.redo(conn)}
          onClearPage={clearPage}
        />
        <main className={`stage ${tool === 'pan' ? 'is-pan' : ''}`} ref={setScrollEl}>
          {doc && self && conn ? (
            <PageView
              doc={doc}
              page={page}
              fit={fit}
              zoom={zoom}
              viewportWidth={viewport.width}
              viewportHeight={viewport.height}
              scrollEl={scrollEl}
              conn={conn}
              history={history}
              self={self}
              canDraw={canDraw}
              isTeacher={isTeacher}
            />
          ) : (
            <div className="stage-loading">
              <div className="spinner" />
              <p>{doc ? 'Derse bağlanılıyor…' : `PDF yükleniyor… ${pdfProgress ? Math.round(pdfProgress * 100) + '%' : ''}`}</p>
            </div>
          )}
        </main>
        {panelOpen && self && conn && me && (
          <ParticipantsPanel
            conn={conn}
            isTeacher={isTeacher}
            selfId={self.id}
            me={me}
            permitted={permitted}
            participants={participants}
            settings={settings}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </div>
      {everOnline && status !== 'online' && (
        <div className="conn-banner" role="status">
          <LuWifiOff /> Bağlantı koptu — yeniden bağlanılıyor. Çizimleriniz sunucuda güvende.
        </div>
      )}
      <Toast />
    </div>
  );
}
